import React, { useState, forwardRef, ForwardRefRenderFunction } from "react";
import { useParams } from "react-router-dom";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
//#endregion
//#region types
import { BusinessUnitFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FormHandles } from "models/propTypes/genericFormProps";
import { BusinessUnitFormContainerProps } from "models/propTypes/businessUnitPropTypes";
import { WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";
import { EntityIdParams } from "models/propTypes/urlParamsTypes";
//#endregion
//#region  components
import { useCheckRelations } from "components/common";
import BusinessUnitForm from "./businessUnitForm";
import { useBusinessUnitService } from "serviceHooks";
//#endregion
import * as Yup from "yup";

const BusinessUnitFormContainer: ForwardRefRenderFunction<FormHandles, BusinessUnitFormContainerProps> = (
  props,
  ref
) => {
  const businessUnitService = useBusinessUnitService();

  const { selectedBusinessUnitChangeHandler } = props;
  const [businessUnit, setBusinessUnit] = React.useState<ApiModel.BusinessUnit>();

  const [excludeMemberIds, setExcludeMemberIds] = useState<number[]>([]);
  const isCreate = !businessUnit?.id;
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const { getIsAdmin } = useCheckRelations();
  const isAdmin = getIsAdmin();

  const urlParams = useParams<EntityIdParams>();

  const loadBusinessUnit = (businessUnitId: string) => {
    if (businessUnitId) {
      businessUnitService.getBusinessUnit(businessUnitId).then((res) => {
        setBusinessUnit(res);
        selectedBusinessUnitChangeHandler(res.name as string);
      });
    }
  };

  React.useEffect(() => {
    const businessUnitId = urlParams.id!;
    loadBusinessUnit(businessUnitId);
  }, [urlParams]);

  const initialValues: BusinessUnitFormTypes.Create = {
    businessUnitName: businessUnit?.name,
    managerId: businessUnit?.managerId,
    managerName: businessUnit?.managerName,
    members: businessUnit?.members,
    managerHasSalaryRights: businessUnit?.managerHasSalaryRights,
  };

  const validationSchema = Yup.object<BusinessUnitFormTypes.Create>({
    businessUnitName: Yup.string().max(100).required(TranslationKeys.Validations.Required),
    managerName: Yup.string().required(TranslationKeys.Validations.Required),
    managerId: Yup.number().required(TranslationKeys.Validations.Required),
    members: Yup.array(),
    managerHasSalaryRights: Yup.boolean(),
  });

  const onFormUpdate = (values: BusinessUnitFormTypes.Create) => {
    selectedBusinessUnitChangeHandler(values.businessUnitName as string);

    const excludeIds: number[] = values.members ? values.members.map((x) => parseInt(x.employeeId!.toString())) : [];
    setExcludeMemberIds(excludeIds);
  };

  const handleSubmit = (values: BusinessUnitFormTypes.Create) => {
    const businessUnitInsert = {
      name: values.businessUnitName,
      managerId: values.managerId,
      members: values.members,
      managerHasSalaryRights: values.managerHasSalaryRights,
    } as ApiModel.BusinessUnitPost;

    if (isCreate) {
      return businessUnitService.createBusinessUnit(businessUnitInsert, dispatch).then((res) => res.data);
    } else {
      const id = businessUnit?.id as string;
      const businessUnitToUpdate = {
        ...businessUnitInsert,
        id: id,
      } as ApiModel.BusinessUnit;

      return businessUnitService.updateBusinessUnit(id, businessUnitToUpdate, dispatch).then((res) => res.data);
    }
  };

  return (
    <BusinessUnitForm
      ref={ref}
      {...props}
      isCreate={isCreate}
      isAdmin={isAdmin}
      validationSchema={validationSchema}
      defaultValues={initialValues}
      handleSubmit={handleSubmit}
      onFormUpdate={onFormUpdate}
      excludeMemberIds={excludeMemberIds}
    />
  );
};

export default forwardRef(BusinessUnitFormContainer);
